/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing quick list modal functionality.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../../config/api';
import { useAuthStore } from '../../../store/auth';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { LogProfile } from '../types';

export function useQuickList() {
  const { user } = useAuthStore();
  const { t } = useI18n();
  const { showError } = useNotifications();
  const tenantSlug = user?.tenant?.slug || t('logProfiles.defaultTenant');

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListProfiles, setQuickListProfiles] = useState<LogProfile[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'name',
        sortOrder: 'asc',
      });

      const response = await fetch(
        buildApiUrl(`${API_CONFIG.ENDPOINTS.LOG_PROFILES}?${params}`),
        {
          method: 'GET',
          headers: getApiHeaders(tenantSlug, user?.id),
        },
      );

      if (!response.ok) {
        throw new Error(t('logProfiles.errors.loadFailed'));
      }

      const result = await response.json();
      const rawProfiles = Array.isArray(result?.data) ? result.data : [];

      // If we got 100 results, try to load more pages
      let allProfiles = [...rawProfiles];
      if (rawProfiles.length === 100 && result?.pagination) {
        const totalPages = result.pagination.pages || Math.ceil((result.pagination.total || 0) / 100);
        for (let page = 2; page <= totalPages && page <= 10; page++) {
          const nextParams = new URLSearchParams({
            page: page.toString(),
            limit: '100',
            sortBy: 'name',
            sortOrder: 'asc',
          });

          const nextResponse = await fetch(
            buildApiUrl(`${API_CONFIG.ENDPOINTS.LOG_PROFILES}?${nextParams}`),
            {
              method: 'GET',
              headers: getApiHeaders(tenantSlug, user?.id),
            },
          );

          if (nextResponse.ok) {
            const nextResult = await nextResponse.json();
            if (Array.isArray(nextResult?.data)) {
              allProfiles = [...allProfiles, ...nextResult.data];
            }
          }
        }
      }

      setQuickListProfiles(allProfiles);
    } catch (error) {
      showError(error?.message || t('logProfiles.errors.loadFailed'));
      setQuickListProfiles([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [tenantSlug, user?.id, showError, t]);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListProfiles.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, quickListProfiles.length, loadQuickList]);

  // Filter profiles client-side
  const filteredQuickList = useMemo(() => {
    const profiles = quickListProfiles.filter((p) => p.id != null);
    if (!quickListSearch.trim()) {
      return profiles;
    }
    const searchLower = quickListSearch.toLowerCase();
    return profiles.filter(
      (profile) =>
        profile.name?.toLowerCase().includes(searchLower) ||
        profile.description?.toLowerCase().includes(searchLower),
    );
  }, [quickListProfiles, quickListSearch]);

  return {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    quickListProfiles,
    isQuickListLoading,
    filteredQuickList,
  };
}

